import React, { useMemo } from 'react';
import { Box, Skeleton } from '@mui/material';

import ConditionRatingsDay from 'components/ConditionRatingsDays/ConditionRatingsDay';
import GraphTimeline, { TimeTickColor } from 'components/GraphTimeline/GraphTimeline';
import ErrorBoundary from 'components/ErrorBoundary';
import ErrorMessage from 'components/ErrorMessage';
import { useDayNightShadingBackground } from 'components/DayNightShading';
import GraphTooltipProvider, {
  GraphId,
} from 'containers/ForecastDataVisualizer/GraphTooltips/GraphTooltipProvider/GraphTooltipProvider';
import { EDGE_THRESHOLD_PX } from 'containers/ForecastDataVisualizer/GraphTooltips/GraphVerticalBar/GraphVerticalBar';
import RatingsTooltip from 'containers/ForecastDataVisualizer/GraphTooltips/RatingsTooltip';
import useCustomError from 'hooks/useCustomError';
import type { ConditionData, RatingDay } from 'types/conditionRating';
import type { RatingTooltipData } from 'types/graphs';
import type { SunlightTimes } from 'types/sunlightTimes';

import styles from './ForecastGraphConditionRatings.module.scss';

/** Satisfies hook when parent has no sunlight yet; background overridden to none. */
const FALLBACK_SUNLIGHT_TIMES: SunlightTimes = { days: [], loading: true };

const ConditionRatingsDayColumn: React.FC<{
  forecastDayIndex: number;
  ratingDay: RatingDay;
  sunlightTimes?: SunlightTimes;
}> = ({ forecastDayIndex, ratingDay, sunlightTimes }) => {
  const { background } = useDayNightShadingBackground({
    sunlightTimes: sunlightTimes ?? FALLBACK_SUNLIGHT_TIMES,
    dayIndex: forecastDayIndex,
  });

  return (
    <Box
      className={styles.dayContainer}
      style={{ background: sunlightTimes ? background : 'none' }}
    >
      <Box className={styles.dayInnerContainer}>
        <ConditionRatingsDay
          day={ratingDay}
          barHeight="8px"
          className={styles.conditionRatingsDay}
        />
        <Box className={styles.timelineContainer}>
          <GraphTimeline
            day={ratingDay}
            showMarkers
            timeTickColor={TimeTickColor.DARK}
            requiresPadding={false}
          />
        </Box>
      </Box>
    </Box>
  );
};

interface ForecastGraphConditionRatingsProps {
  daysToDisplay: number[];
  graphWidth: number;
  leftNowPosition?: number;
  leftPosition?: number;
  ratings: ConditionData;
  renderTooltip: (xPercentage: number, graphId: GraphId) => void;
  timeLabel?: string | null;
  trackGraphScrubbed: (
    graphId: GraphId,
    additionalSegmentProps: { [key: string]: string | number },
  ) => void;
  activeRating?: RatingTooltipData;
  sunlightTimes?: SunlightTimes;
  loading?: boolean;
  dataTestId?: string;
}

const ForecastGraphConditionRatingsContent: React.FC<ForecastGraphConditionRatingsProps> = ({
  daysToDisplay,
  graphWidth,
  leftNowPosition,
  leftPosition,
  ratings,
  renderTooltip,
  timeLabel,
  trackGraphScrubbed,
  activeRating,
  sunlightTimes,
  loading,
  dataTestId = 'forecast-graph-condition-ratings',
}) => {
  const getTooltipPosition = useMemo(
    () => (posX: number, width: number) => {
      if (posX < EDGE_THRESHOLD_PX) return '0';
      if (width - posX < EDGE_THRESHOLD_PX) return '-100%';
      return '-50%';
    },
    [],
  );

  const getTooltipPositionType = useMemo(
    () =>
      (posX: number, width: number): 'left' | 'center' | 'right' => {
        if (posX < EDGE_THRESHOLD_PX) return 'left';
        if (width - posX < EDGE_THRESHOLD_PX) return 'right';
        return 'center';
      },
    [],
  );

  const tooltipContentStyle = useMemo(
    () =>
      activeRating?.data
        ? {
            left: activeRating.leftPosition,
            transform: `translateX(${getTooltipPosition(activeRating.leftPosition, graphWidth)})`,
          }
        : {},
    [activeRating, graphWidth, getTooltipPosition],
  );

  const renderedDays = useMemo(() => {
    if (!ratings?.days || ratings.loading) {
      return null;
    }

    return daysToDisplay.map(
      (day) =>
        ratings.days?.[day] && (
          <ConditionRatingsDayColumn
            key={`condition-ratings-day-${day}`}
            forecastDayIndex={day}
            ratingDay={ratings.days[day] as RatingDay}
            sunlightTimes={sunlightTimes}
          />
        ),
    );
  }, [daysToDisplay, ratings, sunlightTimes]);

  if (loading) {
    return (
      <Skeleton
        animation="wave"
        variant="rectangular"
        data-testid={`${dataTestId}-loading`}
        className={styles.skeletonContainer}
        width="100%"
      />
    );
  }

  if (!ratings?.days || ratings.loading) {
    return null;
  }

  return (
    <Box className={styles.conditionRatingsSection} data-testid={dataTestId}>
      <GraphTooltipProvider
        graphId="condition-ratings"
        customStyle={styles.conditionRatingsVerticalBar}
        customStyleNow={styles.conditionRatingsVerticalBarNow}
        graphWidth={graphWidth}
        leftNowPosition={leftNowPosition}
        leftPosition={leftPosition}
        renderTooltip={renderTooltip}
        timeLabel={timeLabel}
        trackGraphScrubbed={trackGraphScrubbed}
      >
        <Box className={styles.conditionRatingsContainer}>{renderedDays}</Box>
      </GraphTooltipProvider>

      {activeRating?.data && (
        <Box
          className={styles.conditionRatingsTooltipContainer}
          data-testid="condition-ratings-tooltip"
        >
          <Box className={styles.conditionRatingsTooltipContent} style={tooltipContentStyle}>
            <Box
              className={styles.conditionRatingsTooltipPill}
              data-testid="condition-ratings-tooltip-pill"
            >
              <RatingsTooltip
                condition={activeRating.data.rating || ''}
                position={
                  getTooltipPositionType(activeRating.leftPosition, graphWidth) as
                    | 'left'
                    | 'center'
                    | 'right'
                }
              />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export const ForecastGraphConditionRatings: React.FC<ForecastGraphConditionRatingsProps> = (
  props,
) => {
  const { dataTestId = 'forecast-graph-condition-ratings', ratings } = props;
  const { errorMessage, ErrorIcon, hasError } = useCustomError({ error: ratings?.error });

  return (
    <ErrorBoundary
      error={hasError}
      errorBoundaryId="forecast-graph-condition-ratings"
      render={() => (
        <ErrorMessage dataTestId={`${dataTestId}-error`} Icon={ErrorIcon} message={errorMessage} />
      )}
    >
      <ForecastGraphConditionRatingsContent {...props} />
    </ErrorBoundary>
  );
};

export default ForecastGraphConditionRatings;
